import { Box, TextField } from "@mui/material";
import DOMPurify from "dompurify";

import React, { useMemo } from "react";
import { UserMessageTextProps } from "./UserMessage.types";
import { ChatMessageAttachments } from "../ChatMessage";
import ChatMessageImages from "../ChatMessage/ChatMessageImages";

/**
 * Check if text contains HTML that should be rendered
 */
function containsRenderableHtml(text: string): boolean {
  // Check for common HTML elements (tables, divs, spans with style, paragraphs, etc.)
  return /<(table|div|span|p|br|b|strong|i|em|u|ol|ul|li)[\s>]/i.test(text);
}

/**
 * Sanitize HTML for safe rendering
 */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Table elements
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "th",
      "td",
      "caption",
      "colgroup",
      "col",
      // Block elements
      "div",
      "p",
      "br",
      "hr",
      // Inline formatting
      "span",
      "b",
      "strong",
      "i",
      "em",
      "u",
      "s",
      "strike",
      "sub",
      "sup",
      // Lists
      "ul",
      "ol",
      "li",
      // Links (href will be sanitized)
      "a",
      // Headers
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
    ],
    ALLOWED_ATTR: [
      "class",
      "style",
      "colspan",
      "rowspan",
      "scope",
      "headers",
      "href",
      "target",
    ],
  });
}

/** Styling for rendered HTML content */
const htmlStyles = {
  fontSize: "16px",
  lineHeight: 1.6,
  // Table styling
  "& table": {
    borderCollapse: "collapse" as const,
    borderSpacing: 0,
    width: "max-content",
    minWidth: "100%",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    overflow: "hidden",
    my: 1,
  },
  "& th, & td": {
    border: "1px solid",
    borderColor: "divider",
    padding: "8px 12px",
    textAlign: "left" as const,
    whiteSpace: "nowrap" as const,
    maxWidth: "300px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "14px",
  },
  "& th": {
    backgroundColor: "grey.100",
    fontWeight: 600,
    fontSize: "13px",
    color: "text.secondary",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  "& td": {
    backgroundColor: "background.paper",
  },
  "& tr:nth-of-type(even) td": {
    backgroundColor: "grey.50",
  },
  "& tr:hover td": {
    backgroundColor: "action.hover",
  },
  // List styling
  "& ul, & ol": {
    paddingLeft: "20px",
    my: 0.5,
  },
  // Link styling
  "& a": {
    color: "primary.main",
    textDecoration: "underline",
  },
};

/**
 * Check if sanitized HTML has meaningful content (not just whitespace/empty tags)
 */
function hasMeaningfulContent(html: string): boolean {
  if (!html || !html.trim()) return false;

  // Parse and check if there's actual text content
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const textContent = doc.body?.textContent?.trim() || "";

  // Also check for non-text elements that are meaningful (like images, tables with structure)
  const hasTables = doc.querySelector("table") !== null;

  return textContent.length > 0 || hasTables;
}

/**
 * Renders user message content - handles plain text or HTML
 * Falls back to plain text if HTML is malformed or empty after sanitization
 */
const UserMessageContent: React.FC<{ text: string }> = ({ text }) => {
  const { hasHtml, sanitizedContent } = useMemo(() => {
    if (!text) {
      return { hasHtml: false, sanitizedContent: "" };
    }

    if (containsRenderableHtml(text)) {
      const sanitized = sanitizeHtml(text);

      // Check if sanitization produced meaningful content
      if (!hasMeaningfulContent(sanitized)) {
        // HTML was detected but sanitization stripped everything meaningful
        // Fall back to plain text display
        return { hasHtml: false, sanitizedContent: "" };
      }

      return { hasHtml: true, sanitizedContent: sanitized };
    }

    return { hasHtml: false, sanitizedContent: "" };
  }, [text]);

  if (hasHtml) {
    return (
      <Box
        sx={{
          overflowX: "auto",
          maxWidth: "100%",
          ...htmlStyles,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    );
  }

  // Plain text rendering (either no HTML detected, or fallback from bad HTML)
  return (
    <Box fontSize="16px" sx={{ whiteSpace: "pre-wrap" }}>
      {text}
    </Box>
  );
};

const UserMessageText = ({
  messageIndex,
  chatMessage,
  isEditing,
  editedText,
  setEditedText,
  handleSaveUserMessage,
  editedImages,
  onRemoveImage,
}: UserMessageTextProps) => {
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSaveUserMessage();
    }
  };

  return (
    <Box>
      <Box>
        {isEditing ? (
          <>
            <TextField
              fullWidth
              value={editedText}
              onKeyDown={handleKeyDown}
              onChange={handleTextChange}
              variant="outlined"
              size="small"
              id={`chat-bubble-text-${chatMessage?.type}-${messageIndex}`}
              multiline
              minRows={1}
              maxRows={Infinity}
            />
            {/* Display VLM images in edit mode with remove capability */}
            <ChatMessageImages
              images={editedImages}
              isEditing={true}
              onRemoveImage={onRemoveImage}
            />
          </>
        ) : (
          <div className="chat-bubble" id={`chat-bubble-text-${messageIndex}`}>
            <UserMessageContent text={chatMessage.contentParts[0]?.text ?? ""} />
            {/* Display VLM images if they exist (from dialogue history) */}
            <ChatMessageImages images={chatMessage.images} />
            {/* Display file attachments if they exist */}
            <ChatMessageAttachments chatMessage={chatMessage} />
          </div>
        )}
      </Box>
    </Box>
  );
};

export default UserMessageText;
